/**
 * The words a plan is born with: its default title and its tables' names.
 *
 * They are stored in the plan and printed on the PDF, so they belong to the
 * document, in the plan's language — not to the chrome. And they are needed
 * synchronously, by storage when it creates a plan and by the store when a
 * table is added, while the UI catalogues load lazily one language at a time.
 * So the eight languages live here, together, and are a few hundred bytes.
 */

import { LOCALES, type LocaleCode } from './i18n'
import type { EventKind, TableShape } from './types'

export interface PlanWords {
  /** A new plan's title, before anyone renames it. */
  title: Record<EventKind, string>
  /** An ordinary numbered table. */
  table: (n: number) => string
  /** A wedding's own table. */
  couple: string
  /** A head table at anything else — or a wedding's second one. */
  head: string
}

const WORDS: Record<LocaleCode, PlanWords> = {
  en: {
    title: {
      wedding: 'Our Wedding',
      celebration: 'Our Celebration',
      corporate: 'Company Event',
      other: 'Our Event',
    },
    table: (n) => `Table ${n}`,
    couple: 'Bride & Groom',
    head: 'Head table',
  },
  az: {
    title: {
      wedding: 'Toyumuz',
      celebration: 'Məclisimiz',
      corporate: 'Korporativ tədbir',
      other: 'Tədbirimiz',
    },
    table: (n) => `Masa ${n}`,
    couple: 'Bəy və gəlin',
    head: 'Baş masa',
  },
  tr: {
    title: {
      wedding: 'Düğünümüz',
      celebration: 'Kutlamamız',
      corporate: 'Şirket etkinliği',
      other: 'Etkinliğimiz',
    },
    table: (n) => `Masa ${n}`,
    couple: 'Gelin ve Damat',
    head: 'Baş masa',
  },
  ru: {
    title: {
      wedding: 'Наша свадьба',
      celebration: 'Наш праздник',
      corporate: 'Корпоратив',
      other: 'Наше мероприятие',
    },
    table: (n) => `Стол ${n}`,
    couple: 'Жених и невеста',
    head: 'Главный стол',
  },
  ar: {
    title: {
      wedding: 'زفافنا',
      celebration: 'احتفالنا',
      corporate: 'فعالية الشركة',
      other: 'مناسبتنا',
    },
    table: (n) => `طاولة ${n}`,
    couple: 'العروسان',
    head: 'الطاولة الرئيسية',
  },
  es: {
    title: {
      wedding: 'Nuestra boda',
      celebration: 'Nuestra celebración',
      corporate: 'Evento de empresa',
      other: 'Nuestro evento',
    },
    table: (n) => `Mesa ${n}`,
    couple: 'Los novios',
    head: 'Mesa presidencial',
  },
  zh: {
    title: {
      wedding: '我们的婚礼',
      celebration: '我们的庆典',
      corporate: '公司活动',
      other: '我们的活动',
    },
    table: (n) => `${n}号桌`,
    couple: '新郎新娘',
    head: '主桌',
  },
  fr: {
    title: {
      wedding: 'Notre mariage',
      celebration: 'Notre fête',
      corporate: 'Événement d’entreprise',
      other: 'Notre événement',
    },
    table: (n) => `Table ${n}`,
    couple: 'Les mariés',
    head: 'Table d’honneur',
  },
}

export const planWords = (language: LocaleCode): PlanWords => WORDS[language] ?? WORDS.en

/**
 * The name a new table of `shape` gets in this plan.
 *
 * Numbered tables take the lowest number nobody uses, so deleting table 3 makes
 * the next one table 3 again. A number counts as used in *any* language: a plan
 * started in English and switched to Russian has "Table 3", and must not grow a
 * "Стол 3" beside it.
 */
export function nextTableName(
  tables: ReadonlyArray<{ name: string; shape: TableShape }>,
  shape: TableShape,
  kind: EventKind,
  language: LocaleCode,
): string {
  const words = planWords(language)
  const names = new Set(tables.map((table) => table.name.trim()))
  if (shape === 'sweetheart') {
    if (kind === 'wedding' && !tables.some((table) => table.shape === 'sweetheart')) {
      return words.couple
    }
    if (!names.has(words.head)) return words.head
  }
  let n = 1
  while (LOCALES.some((locale) => names.has(planWords(locale).table(n)))) n += 1
  return words.table(n)
}

/**
 * A table read off a venue's chart, named in the plan's language. The detector
 * says "Table N" — it is a port of the server's and its output is held to the
 * server's by the parity script — so the translation happens on the way in.
 */
export function localTableName(name: string, language: LocaleCode): string {
  const match = /^Table (\d+)$/.exec(name)
  return match ? planWords(language).table(Number(match[1])) : name
}
